import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ExternalLink, Edit2, Trash2, Save, X, Star } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface Resource {
  title: string;
  url: string;
  type: string;
  description: string;
  isPrimary: boolean;
}

interface ResourcePreviewProps {
  resources: Array<{
    moduleId: string;
    courseId: string;
    moduleTitle: string;
    resources: Resource[];
  }>;
  onRefresh: () => void;
}

export const ResourcePreview: React.FC<ResourcePreviewProps> = ({ resources, onRefresh }) => {
  const [editingModule, setEditingModule] = useState<string | null>(null);
  const [editedResources, setEditedResources] = useState<Resource[]>([]);

  const startEdit = (moduleId: string, currentResources: Resource[]) => {
    setEditingModule(moduleId);
    setEditedResources(JSON.parse(JSON.stringify(currentResources)));
  };

  const cancelEdit = () => {
    setEditingModule(null);
    setEditedResources([]);
  };

  const saveEdit = async (moduleId: string, courseId: string) => {
    try {
      const { error } = await supabase
        .from('module_content')
        .update({ further_reading: editedResources as any })
        .eq('module_id', moduleId)
        .eq('course_id', courseId);

      if (error) throw error;

      toast.success('Resources updated successfully');
      setEditingModule(null);
      onRefresh();
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Failed to update resources');
    }
  };

  const updateResource = (index: number, field: keyof Resource, value: any) => {
    const updated = [...editedResources];
    updated[index] = { ...updated[index], [field]: value };
    setEditedResources(updated);
  };

  const removeResource = (index: number) => {
    setEditedResources(editedResources.filter((_, i) => i !== index));
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'documentation': return 'bg-blue-500/10 text-blue-600';
      case 'tool': return 'bg-green-500/10 text-green-600';
      case 'reference': return 'bg-purple-500/10 text-purple-600';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (resources.length === 0) {
    return (
      <Card className="p-12 text-center">
        <p className="text-muted-foreground">No generated resources to preview yet.</p>
        <p className="text-sm text-muted-foreground mt-2">
          Generate resources for a category to see them here.
        </p>
      </Card>
    );
  }

  return (
    <ScrollArea className="h-[600px]">
      <div className="space-y-4 pr-4">
        {resources.map((item) => {
          const isEditing = editingModule === item.moduleId;
          const displayResources = isEditing ? editedResources : item.resources;

          return (
            <Card key={item.moduleId} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{item.moduleTitle}</h3>
                  <p className="text-sm text-muted-foreground">
                    {item.moduleId} • {item.courseId}
                  </p>
                </div>
                
                {!isEditing ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startEdit(item.moduleId, item.resources)}
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelEdit}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => saveEdit(item.moduleId, item.courseId)}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {displayResources.map((resource, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-muted/50 rounded-lg space-y-2"
                  >
                    {isEditing ? (
                      <>
                        <div className="flex gap-2">
                          <Input
                            value={resource.title}
                            onChange={(e) => updateResource(idx, 'title', e.target.value)}
                            placeholder="Resource title"
                            className="flex-1"
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => removeResource(idx)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <Input
                          value={resource.url}
                          onChange={(e) => updateResource(idx, 'url', e.target.value)}
                          placeholder="URL"
                        />
                        <Input
                          value={resource.description}
                          onChange={(e) => updateResource(idx, 'description', e.target.value)}
                          placeholder="Description"
                        />
                        <div className="flex items-center gap-2">
                          <select
                            value={resource.type}
                            onChange={(e) => updateResource(idx, 'type', e.target.value)}
                            className="px-3 py-2 rounded-md border bg-background"
                          >
                            <option value="documentation">Documentation</option>
                            <option value="tool">Tool</option>
                            <option value="reference">Reference</option>
                          </select>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={resource.isPrimary}
                              onChange={(e) => updateResource(idx, 'isPrimary', e.target.checked)}
                            />
                            <span className="text-sm">Primary Resource</span>
                          </label>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{resource.title}</h4>
                            {resource.isPrimary && (
                              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                            )}
                          </div>
                          <Badge variant="secondary" className={getTypeColor(resource.type)}>
                            {resource.type}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{resource.description}</p>
                        <a
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          {resource.url}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </ScrollArea>
  );
};
